/* 3D and AR pilot (snippets/pl-pdp-3d, 2026-10-20). Loaded only on product pages with a 3D model.
 *
 * 1. The 3D frame. A tap on its poster (a swipe changes photo: it makes no click) loads Shopify's model viewer
 *    (Shopify.loadFeatures 'model-viewer-ui', the component main-product's model_viewer_tag needs, as Dawn does;
 *    nothing loads before the tap), puts the frame's model-viewer in and starts it. The frame is marked is-live while
 *    the model runs, so pl-product.js leaves drags to it; a tap stops it (toggleable) and leaving the frame stops it,
 *    and swipes change photo again.
 * 2. "View in your room", at true size with no pinch-resize (owner): AR Quick Look on iPhone and iPad (the rel="ar"
 *    link to the USDZ, #allowsContentScaling=0), Scene Viewer on Android (ar_only, resizable=false). Other phones get
 *    nothing, desktops a note. A tap sends gtag('event', 'ar_open', {product, platform}) when gtag is on the page.
 * Gate: a row marked data-pl-ar-gate (product not tagged ar-live) shows only with ?ar=1 in the URL. */
(() => {
  'use strict';
  const VIEWER_CSS = 'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';
  const NO_AR = '#pl-no-ar';
  const unlocked = /[?&]ar=1(?:&|$)/.test(window.location.search);

  /* ------------------------------ 3D frame ------------------------------ */
  let loading = null;
  function viewerReady() {
    if (window.Shopify && window.Shopify.ModelViewerUI) return Promise.resolve();
    if (!loading) {
      loading = new Promise((resolve, reject) => {
        const shopify = window.Shopify;
        if (!shopify || typeof shopify.loadFeatures !== 'function') {
          reject(new Error('Shopify.loadFeatures missing'));
          return;
        }
        if (!document.querySelector(`link[href="${VIEWER_CSS}"]`)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = VIEWER_CSS;
          document.head.appendChild(link);
        }
        shopify.loadFeatures([{name: 'model-viewer-ui', version: '1.0', onLoad: (errors) => (errors ? reject(errors) : resolve())}]);
      });
      /* a failed load can be tried again with the next tap */
      loading.catch(() => {
        loading = null;
      });
    }
    return loading;
  }

  function startModel(frame) {
    if (frame.classList.contains('is-ready') || frame.classList.contains('is-loading')) return;
    const poster = frame.querySelector('[data-pl-model-play]');
    const template = frame.querySelector('template[data-pl-model-tpl]');
    const source = template && template.content.querySelector('model-viewer');
    if (!source) return;
    frame.classList.add('is-loading');
    if (poster) poster.setAttribute('aria-busy', 'true');
    viewerReady()
      .then(() => {
        const viewer = document.importNode(source, true);
        let stoppedAt = 0;
        viewer.addEventListener('shopify_model_viewer_ui_toggle_play', () => frame.classList.add('is-live'));
        viewer.addEventListener('shopify_model_viewer_ui_toggle_pause', () => {
          frame.classList.remove('is-live');
          stoppedAt = Date.now();
        });
        /* a tap stops the model on touchend; the click that follows lands on Shopify's play button, which has just come
           up under the finger, and would start it again: that one click is dropped */
        frame.addEventListener('click', (event) => {
          if (Date.now() - stoppedAt < 600 && event.target.closest('.shopify-model-viewer-ui__button--poster')) {
            event.stopPropagation();
            event.preventDefault();
          }
        }, true);
        frame.appendChild(viewer);
        const ui = new window.Shopify.ModelViewerUI(viewer);
        frame.classList.add('is-ready');
        if (poster) poster.hidden = true;
        /* the gallery hides the frame when another photo comes up: the model stops, so it is still on return */
        new MutationObserver(() => {
          if (frame.hidden && ui.interacting) ui.pause();
        }).observe(frame, {attributes: true, attributeFilter: ['hidden']});
        if (!frame.hidden) ui.play();
      })
      .catch(() => {})
      .finally(() => {
        frame.classList.remove('is-loading');
        if (poster) poster.removeAttribute('aria-busy');
      });
  }

  /* ---------------------------- View in your room ---------------------------- */
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  /* as model-viewer decides: Safari says so through relList; Chrome, Edge, Firefox and the Google app on iOS run
     Quick Look without saying it */
  const quickLook = isIOS && (() => {
    const probe = document.createElement('a');
    return Boolean(probe.relList && probe.relList.supports && probe.relList.supports('ar')) ||
      /CriOS\/|EdgiOS\/|FxiOS\/|GSA\/|DuckDuckGo\//.test(ua);
  })();
  const sceneViewer = !isIOS && /android/i.test(ua) && !/firefox/i.test(ua) && !/OculusBrowser/.test(ua);
  const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  /* Scene Viewer through Google Play Services for AR (developers.google.com/ar/develop/scene-viewer, the AR-only
     intent); without it Chrome opens the fallback: this page with #pl-no-ar, which hides the button */
  function sceneViewerUrl(row) {
    const file = new URL(row.dataset.glb, window.location.href).href;
    const back = new URL(window.location.href);
    back.hash = NO_AR;
    const query = `file=${encodeURIComponent(file)}&mode=ar_only&resizable=false&title=${encodeURIComponent(row.dataset.title || '')}`;
    return `intent://arvr.google.com/scene-viewer/1.2?${query}#Intent;scheme=https;package=com.google.ar.core;` +
      `action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(back.href)};end;`;
  }

  function setupRow(row, handle) {
    const ios = row.querySelector('[data-pl-ar-open="ios"]');
    const android = row.querySelector('[data-pl-ar-open="android"]');
    const note = row.querySelector('[data-pl-ar-note]');
    let shown = null;
    if (quickLook && ios) shown = ios;
    else if (sceneViewer && android && row.dataset.glb) shown = android;
    if (shown) {
      if (shown === android) android.href = sceneViewerUrl(row);
      shown.hidden = false;
      shown.addEventListener('click', () => {
        /* the page address may have changed (?variant=): the fallback comes back to it */
        if (shown === android) android.href = sceneViewerUrl(row);
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'ar_open', {product: handle, platform: shown === ios ? 'ios' : 'android'});
        }
      });
    } else if (!coarse && note) {
      note.hidden = false;
    } else {
      return;
    }
    row.hidden = false;

    /* Android without Google Play Services for AR: Chrome came back to #pl-no-ar */
    const noAr = () => {
      if (window.location.hash !== NO_AR || shown !== android) return;
      row.hidden = true;
      history.replaceState(history.state, '', window.location.pathname + window.location.search);
    };
    noAr();
    window.addEventListener('hashchange', noAr);
  }

  for (const root of document.querySelectorAll('[data-pl-product]')) {
    for (const frame of root.querySelectorAll('[data-pl-model]')) {
      frame.addEventListener('click', (event) => {
        if (event.target.closest('[data-pl-model-play]')) startModel(frame);
      });
    }
    const row = root.querySelector('[data-pl-ar]');
    if (row && (unlocked || !row.hasAttribute('data-pl-ar-gate'))) setupRow(row, root.dataset.handle || '');
  }
})();
